import { useCallback, useEffect, useMemo, useState } from 'react'
import { Table, Switch, message } from 'antd'
import { useDispatch } from 'react-redux'
import { PageHeader } from '../Common'
import { useIsMobile } from '../../hooks'
import {
  fetchPracticeFeatures,
  usePracticeFeatures,
  toggleFeatureFlag,
} from '../../redux/slices/practice-features'
import {
  EXPAND_COLUMN_WIDTH,
  PracticeFeaturesMobileCard,
  PracticeFeaturesMobileDetails,
} from './PracticeFeaturesMobileRow'

// Defined at module scope so the antd render prop isn't flagged as an
// inline-defined component (it just returns an existing component element).
const renderMobileDetails =
  (featureFlags, loading, onToggle, indent) => practice => (
    <PracticeFeaturesMobileDetails
      practice={practice}
      featureFlags={featureFlags}
      loading={loading}
      onToggle={onToggle}
      indent={indent}
    />
  )

export default function PracticeFeatures() {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const { practices, featureFlags } = usePracticeFeatures()
  const [loading, setLoading] = useState('none')

  useEffect(() => {
    setLoading('fetch')
    dispatch(fetchPracticeFeatures())
    setLoading('none')
  }, [dispatch])

  const handleToggle = useCallback(
    async (practiceId, featureFlagId, enabled) => {
      setLoading(`toggle-${practiceId}-${featureFlagId}`)
      try {
        await toggleFeatureFlag(practiceId, featureFlagId, enabled)
        await dispatch(fetchPracticeFeatures())
        message.success('Feature flag updated successfully')
      } catch (e) {
        message.error(`Error updating feature flag: ${e.message}`)
      } finally {
        setLoading('none')
      }
    },
    [dispatch],
  )

  const columns = [
    {
      title: 'Practice',
      dataIndex: 'name',
      key: 'name',
      width: 300,
    },
    ...(featureFlags?.map(flag => ({
      title: flag.label,
      key: flag.id,
      width: 200,
      render: (_, practice) => {
        const isEnabled = flag.practices.some(p => p.id === practice.id)
        return (
          <Switch
            onChange={checked => handleToggle(practice.id, flag.id, checked)}
            loading={loading === `toggle-${practice.id}-${flag.id}`}
            defaultChecked={isEnabled}
          />
        )
      },
    })) || []),
  ]

  // The desktop table grows one column per feature flag, and flags are created
  // by admins, so the row is unbounded in width. On mobile it collapses to a
  // card with the practice name and a summary of the flags that are on; the
  // switches themselves live behind the per-row expand toggle.
  const mobileColumns = useMemo(
    () => [
      {
        title: 'Practice',
        key: 'practice',
        render(_, practice) {
          return (
            <PracticeFeaturesMobileCard
              practice={practice}
              featureFlags={featureFlags || []}
            />
          )
        },
      },
    ],
    [featureFlags],
  )

  const mobileExpandable = useMemo(
    () => ({
      columnWidth: EXPAND_COLUMN_WIDTH,
      // Nothing to toggle when no flag exists yet, so hide the expand icon.
      rowExpandable: () => Boolean(featureFlags?.length),
      expandedRowRender: renderMobileDetails(
        featureFlags || [],
        loading,
        handleToggle,
        EXPAND_COLUMN_WIDTH,
      ),
    }),
    [featureFlags, loading, handleToggle],
  )

  return (
    <div>
      <PageHeader title="Practice Features" />
      <Table
        size={isMobile ? 'small' : 'middle'}
        rowKey="id"
        style={{ overflowX: 'auto' }}
        loading={loading === 'fetch'}
        dataSource={practices}
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        expandable={isMobile ? mobileExpandable : undefined}
      />
    </div>
  )
}
